import { useState } from 'react';
import {
  SearchSelect,
  StatCard,
  StatCardList,
  Table,
  cn,
  type Column,
} from '@evogard/ui';
import type { ManagementPeripherals } from '@evogard/contract';
import {
  CLAIM_FLOW_DEFAULT_PERIOD,
  CLAIM_FLOW_PERIODS,
  PERIPHERAL_ITEM_TYPES,
  count,
  format,
  fromColumn,
  peripheralItemTypeLabel,
  type ClaimFlowPeriod,
  type PeripheralWeekOrigin,
} from '@evogard/domain';
import { DonutFigure, donutToneAt } from './Donut.js';
import { Ranking, StatesFigure, type StateNoun } from './ClaimStatesMap.js';
import { PeripheralMoneyChart, PeripheralQuotesChart } from './PeripheralMoneyCharts.js';
import { LINE_TONE, stageLabel } from './managementLabels.js';
import { PanelCard } from './PanelCard.js';
import { useManagementPeripherals } from './useManagement.js';

/**
 * O Painel da Gestão dos periféricos (28/10/2026): os gráficos que o dono
 * escolheu para Periféricos — tempo e prazo por etapa, acionamentos por
 * semana, o dinheiro, as cotações, os itens e os estados.
 *
 * A forma é a do Painel da Gestão do sinistro: um período no alto, que vale
 * para todos, os números de relance, e os cartões numa grade de duas colunas.
 * A diferença é a unidade: Periféricos é um departamento só, e o tempo e o
 * prazo descem à etapa. "O fluxo do sinistro" não tem par aqui — é a
 * passagem entre departamentos, e Periféricos não passa por outro.
 *
 * Os dados chegam numa resposta só (`useManagementPeripherals`), e cada
 * cartão lê a sua parte.
 */

type Data = ManagementPeripherals;
type Query = ReturnType<typeof useManagementPeripherals>;

export function PeripheralPanel() {
  const [days, setDays] = useState<ClaimFlowPeriod>(CLAIM_FLOW_DEFAULT_PERIOD);
  const query = useManagementPeripherals(days);
  return (
    <div className="flex flex-col gap-3.5">
      <div className="w-full sm:w-64">
        <SearchSelect
          label="Período"
          options={CLAIM_FLOW_PERIODS.map((d) => ({
            value: String(d),
            label: `Abertos nos últimos ${d} dias`,
          }))}
          value={String(days)}
          onChange={(v) => v && setDays(Number(v) as ClaimFlowPeriod)}
        />
      </div>
      <Highlights data={query.data} />
      <div className="grid gap-3.5 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <WeeksChart query={query} days={days} />
        </div>
        <DeadlinesChart query={query} days={days} />
        <DurationsChart query={query} days={days} />
        <PeripheralMoneyChart query={query} days={days} />
        <PeripheralQuotesChart query={query} days={days} />
        <ItemTypesChart query={query} days={days} />
        <StatesChart query={query} days={days} />
      </div>
    </div>
  );
}

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const money = (text: string) => format(fromColumn(text)!);

/** Os números de relance, da mesma resposta dos gráficos. Esqueleto até ela chegar. */
function Highlights({ data }: { data: Data | undefined }) {
  const working = data?.deadlines.reduce(
    (n, s) => n + s.withinDeadline + s.expired + s.noDeadline,
    0,
  );
  const expired = data?.deadlines.reduce((n, s) => n + s.expired, 0);
  return (
    <StatCardList stretch>
      <StatCard
        icon="documento"
        density="densa"
        label="Acionamentos no período"
        value={data?.total}
        support={data ? `${data.inProcess} em processo` : ''}
      />
      <StatCard
        icon="atencao"
        density="densa"
        label="SLA expirado"
        value={expired}
        tone={expired ? 'erro' : 'neutro'}
        support={
          working === undefined || expired === undefined
            ? ''
            : `${share(expired, working)}% dos que estão numa etapa`
        }
      />
      <StatCard
        icon="dinheiro"
        density="densa"
        label="Total aprovado"
        value={data ? money(data.money.totalApproved) : undefined}
        support={data ? `${money(data.money.associationShare)} da associação` : ''}
      />
      <StatCard
        icon="pessoa"
        density="densa"
        label="Pago pelos associados"
        value={data ? money(data.money.memberShare) : undefined}
        support="a soma das PIs dos acionamentos com valor"
      />
    </StatCardList>
  );
}

/* ------------------------------------------------------------------------ */

const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
function sundayOf(monday: string): string {
  const d = new Date(`${monday}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 6);
  return d.toISOString().slice(0, 10);
}
const rangeOf = (w: Week) => `${ddmm(w.week)} a ${ddmm(sundayOf(w.week))}`;

type Week = Data['weeks'][number];
const weekTotal = (w: Week) =>
  w.origins.chatbot + w.origins.administrativo + w.origins.portal + w.origins.unknown;

/** As origens, de baixo para cima na barra, com a cor e o nome de cada uma. */
const ORIGINS: { key: PeripheralWeekOrigin; label: string; className: string }[] = [
  { key: 'chatbot', label: 'chatbot', className: 'bg-primaria' },
  { key: 'administrativo', label: 'administrativo', className: 'bg-fase-azul' },
  { key: 'portal', label: 'portal do associado', className: 'bg-fase-turquesa' },
  { key: 'unknown', label: 'sem origem registrada', className: 'bg-texto-tenue' },
];

/**
 * Os acionamentos por semana, empilhados pela origem do pedido. Toda semana
 * aparece, a vazia também, e as incompletas das pontas ficam mais claras.
 */
function WeeksChart({ query, days }: { query: Query; days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const data = query.data;
  const total = data ? data.weeks.reduce((n, w) => n + weekTotal(w), 0) : 0;
  return (
    <PanelCard
      title="Acionamentos por semana"
      descricao="Abertos por semana, de segunda a domingo, pela origem do pedido; as semanas incompletas das pontas ficam mais claras."
      query={query}
      empty={total === 0 ? `Nenhum acionamento aberto nos últimos ${days} dias.` : null}
      asTable={asTable}
      onToggle={() => setAsTable((v) => !v)}
    >
      {data &&
        (asTable ? (
          <Table
            columns={
              [
                {
                  title: 'Semana',
                  cell: (r) => `${rangeOf(r)}${r.partial ? ' (incompleta)' : ''}`,
                  alignment: 'inicio',
                },
                { title: 'Acionamentos', cell: weekTotal, alignment: 'fim' },
                ...ORIGINS.map((o): Column<Week> => ({
                  title: o.label[0]!.toUpperCase() + o.label.slice(1),
                  cell: (r) => r.origins[o.key],
                  alignment: 'fim',
                })),
              ] satisfies Column<Week>[]
            }
            items={data.weeks}
            rowKey={(r) => r.week}
            label={`Acionamentos por semana nos últimos ${data.days} dias`}
            empty="Nenhuma semana no período."
          />
        ) : (
          <WeeksFigure data={data} total={total} />
        ))}
    </PanelCard>
  );
}

function WeeksFigure({ data, total }: { data: Data; total: number }) {
  const [hover, setHover] = useState<Week | null>(null);
  const max = Math.max(1, ...data.weeks.map(weekTotal));
  const every = Math.ceil(data.weeks.length / 13);
  const full = data.weeks.filter((w) => !w.partial);
  const mean = full.length ? full.reduce((n, w) => n + weekTotal(w), 0) / full.length : 0;
  /* Só as origens que apareceram no período vão para a legenda. */
  const present = ORIGINS.filter((o) => data.weeks.some((w) => w.origins[o.key] > 0));

  return (
    <div className="flex flex-col gap-3">
      <p className="min-h-5 text-sm text-texto-suave" aria-live="polite">
        {hover ? (
          <>
            Semana de {rangeOf(hover)}:{' '}
            <b className="font-semibold text-texto">
              {count(weekTotal(hover), 'acionamento', 'acionamentos')}
            </b>
            {present.length > 1 &&
              ` (${present
                .filter((o) => hover.origins[o.key] > 0)
                .map((o) => `${hover.origins[o.key]} ${o.label}`)
                .join(', ')})`}
            {hover.partial && ' — semana incompleta'}
          </>
        ) : (
          <>
            <b className="font-semibold text-texto">
              {count(total, 'acionamento', 'acionamentos')}
            </b>{' '}
            no período
            {full.length > 0 && (
              <> · média de {Math.round(mean * 10) / 10} por semana completa</>
            )}
          </>
        )}
      </p>
      <div
        className="flex h-36 items-end gap-1"
        role="img"
        aria-label={`Acionamentos por semana: ${data.weeks
          .map((w) => `semana de ${ddmm(w.week)}, ${weekTotal(w)}`)
          .join('; ')}.`}
        onPointerLeave={() => setHover(null)}
      >
        {data.weeks.map((w) => (
          <div
            key={w.week}
            className="flex h-full min-w-0 flex-1 cursor-default flex-col justify-end"
            onPointerEnter={() => setHover(w)}
          >
            <div
              className={cn(
                'flex flex-col-reverse overflow-hidden rounded-t-[3px] transition-opacity',
                w.partial && 'opacity-45',
                hover && hover.week !== w.week && 'opacity-60',
              )}
              style={{ height: `${(weekTotal(w) / max) * 100}%` }}
            >
              {ORIGINS.map((o) => (
                <span
                  key={o.key}
                  className={o.className}
                  style={{ flex: w.origins[o.key] }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-1 border-t border-borda pt-1.5" aria-hidden>
        {data.weeks.map((w, i) => (
          <span
            key={w.week}
            className="min-w-0 flex-1 overflow-visible whitespace-nowrap text-[11px] text-texto-suave tabular-nums"
          >
            {i % every === 0 ? ddmm(w.week) : ''}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-texto-suave">
        {present.map((o) => (
          <span key={o.key} className="flex items-center gap-1.5">
            <i aria-hidden className={cn('size-2.5 rounded-[3px]', o.className)} />
            {o.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */

type Deadline = Data['deadlines'][number];
const deadlineTotal = (s: Deadline) => s.withinDeadline + s.expired + s.noDeadline;
const phaseName = (phase: string) => stageLabel('perifericos', phase);

/**
 * O prazo por etapa: quem está em cada etapa agora. O vermelho é o SLA
 * expirado — o mesmo do mapa —, o verde está dentro do prazo e o cinza está
 * numa etapa sem SLA configurado. O número escrito não depende da cor.
 */
function DeadlinesChart({ query, days }: { query: Query; days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const data = query.data;
  const working = data ? data.deadlines.reduce((n, s) => n + deadlineTotal(s), 0) : 0;
  const expired = data ? data.deadlines.reduce((n, s) => n + s.expired, 0) : 0;
  const max = Math.max(1, ...(data?.deadlines.map(deadlineTotal) ?? []));
  const pct = (n: number) => `${(n / max) * 100}%`;

  return (
    <PanelCard
      title="Prazo por etapa"
      descricao="Quem está em cada etapa agora, e quantos já passaram do SLA da fase."
      query={query}
      empty={
        working === 0
          ? `Nenhum acionamento aberto nos últimos ${days} dias está numa etapa de trabalho agora.`
          : null
      }
      asTable={asTable}
      onToggle={() => setAsTable((v) => !v)}
      caption={
        <>
          {count(working, 'acionamento está', 'acionamentos estão')} numa etapa de
          trabalho;{' '}
          {expired > 0 ? (
            <b className="font-semibold text-erro">
              {expired} com o SLA expirado ({share(expired, working)}%)
            </b>
          ) : (
            'nenhum com o SLA expirado'
          )}
          .
        </>
      }
    >
      {data &&
        (asTable ? (
          <Table
            columns={
              [
                { title: 'Etapa', cell: (r) => phaseName(r.phase), alignment: 'inicio' },
                { title: 'Na etapa', cell: deadlineTotal, alignment: 'fim' },
                { title: 'SLA expirado', cell: (r) => r.expired, alignment: 'fim' },
                {
                  title: 'Dentro do prazo',
                  cell: (r) => r.withinDeadline,
                  alignment: 'fim',
                },
                { title: 'Sem SLA', cell: (r) => r.noDeadline, alignment: 'fim' },
              ] satisfies Column<Deadline>[]
            }
            items={data.deadlines}
            rowKey={(r) => r.phase}
            label={`Prazo por etapa dos abertos nos últimos ${data.days} dias`}
            empty="Nenhum acionamento numa etapa de trabalho."
          />
        ) : (
          <div className="flex flex-col gap-3">
            <ol className="flex flex-col gap-1">
              {data.deadlines.map((s) => (
                <li
                  key={s.phase}
                  className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,6.5rem)] items-center gap-3 text-xs"
                >
                  <span className="truncate text-texto-suave">{phaseName(s.phase)}</span>
                  <span className="flex h-2.5 overflow-hidden rounded-pilula bg-superficie-suave">
                    <span className="h-full bg-erro" style={{ width: pct(s.expired) }} />
                    <span
                      className="h-full bg-sucesso"
                      style={{ width: pct(s.withinDeadline) }}
                    />
                    <span
                      className="h-full bg-texto-tenue"
                      style={{ width: pct(s.noDeadline) }}
                    />
                  </span>
                  <span className="text-right tabular-nums text-texto-suave">
                    <b className="font-semibold text-texto">{deadlineTotal(s)}</b>
                    {s.expired > 0 && (
                      <span className="ml-1.5 text-erro">· {s.expired} expirado</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-texto-suave">
              <Swatch className="bg-erro">SLA expirado</Swatch>
              <Swatch className="bg-sucesso">dentro do prazo</Swatch>
              <Swatch className="bg-texto-tenue">etapa sem SLA</Swatch>
            </div>
          </div>
        ))}
    </PanelCard>
  );
}

function Swatch({ className, children }: { className: string; children: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <i aria-hidden className={cn('h-2.5 w-4 rounded-pilula', className)} />
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------------ */

const days1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const inDays = (n: number) => `${days1.format(n)} ${n === 1 ? 'dia' : 'dias'}`;
const orDash = (n: number | null) => (n === null ? '—' : inDays(n));

type Duration = Data['durations'][number];

/**
 * O tempo por etapa: a barra é a MÉDIA de quem já saiu, e o traço sobre ela é
 * a MEDIANA — os dois juntos dizem se a etapa é lenta ou se tem uns poucos
 * parados. Quem ainda está na etapa vem escrito ao lado, porque ainda não tem
 * tempo final. As sete etapas aparecem sempre, na ordem do fluxo.
 */
function DurationsChart({ query, days }: { query: Query; days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const data = query.data;
  const any = !!data?.durations.some((d) => d.done + d.inProgress > 0);
  const max = Math.max(
    1,
    ...(data?.durations.flatMap((d) => [d.meanDays ?? 0, d.medianDays ?? 0]) ?? []),
  );
  const pct = (n: number) => `${(n / max) * 100}%`;
  const tone = LINE_TONE.perifericos;

  return (
    <PanelCard
      title="Tempo por etapa"
      descricao="Dias em cada etapa: média e mediana de quem já saiu, e quantos ainda estão nela."
      query={query}
      empty={
        any
          ? null
          : `Nenhum acionamento aberto nos últimos ${days} dias passou por uma etapa ainda.`
      }
      asTable={asTable}
      onToggle={() => setAsTable((v) => !v)}
    >
      {data &&
        (asTable ? (
          <Table
            columns={
              [
                { title: 'Etapa', cell: (r) => phaseName(r.phase), alignment: 'inicio' },
                { title: 'Já saíram', cell: (r) => r.done, alignment: 'fim' },
                { title: 'Média', cell: (r) => orDash(r.meanDays), alignment: 'fim' },
                { title: 'Mediana', cell: (r) => orDash(r.medianDays), alignment: 'fim' },
                { title: 'Ainda lá', cell: (r) => r.inProgress, alignment: 'fim' },
                {
                  title: 'Há (média)',
                  cell: (r) => orDash(r.inProgressMeanDays),
                  alignment: 'fim',
                },
              ] satisfies Column<Duration>[]
            }
            items={data.durations}
            rowKey={(r) => r.phase}
            label={`Tempo por etapa nos últimos ${data.days} dias`}
            empty="Nenhum acionamento passou por uma etapa no período."
          />
        ) : (
          <div className="flex flex-col gap-3">
            <ol className="flex flex-col gap-2" aria-label="Tempo médio por etapa">
              {data.durations.map((d) => (
                <li
                  key={d.phase}
                  className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,7.5rem)] items-center gap-3 text-xs"
                >
                  <span className="truncate text-texto-suave">{phaseName(d.phase)}</span>
                  <span className="relative h-3 rounded-pilula bg-superficie-suave">
                    {d.meanDays !== null && (
                      <span
                        className={cn(
                          'absolute inset-y-0 left-0 rounded-pilula',
                          tone.fill,
                        )}
                        style={{ width: pct(d.meanDays) }}
                      />
                    )}
                    {d.medianDays !== null && (
                      <span
                        aria-hidden
                        title={`Mediana: ${inDays(d.medianDays)}`}
                        className="absolute -inset-y-1 w-0.5 rounded-pilula bg-texto"
                        style={{ left: `calc(${pct(d.medianDays)} - 1px)` }}
                      />
                    )}
                  </span>
                  <span className="text-right tabular-nums text-texto-suave">
                    {d.meanDays === null ? (
                      '—'
                    ) : (
                      <b className="font-semibold text-texto">{inDays(d.meanDays)}</b>
                    )}
                    {d.inProgress > 0 && (
                      <span
                        className="ml-1.5"
                        title={`${count(d.inProgress, 'ainda está', 'ainda estão')} nela, há ${inDays(d.inProgressMeanDays!)} em média`}
                      >
                        · {d.inProgress} lá
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-texto-suave">
              <Swatch className={tone.fill}>média de quem já saiu</Swatch>
              <span className="flex items-center gap-1.5">
                <i aria-hidden className="h-3.5 w-0.5 rounded-pilula bg-texto" />
                mediana
              </span>
              <span>"lá": quantos ainda estão na etapa</span>
            </div>
          </div>
        ))}
    </PanelCard>
  );
}

/* ------------------------------------------------------------------------ */

/* A cor do tipo, pela ordem da lista de tipos; fora dela (dado antigo), cinza. */
const ITEM_TYPE_ORDER = Object.keys(PERIPHERAL_ITEM_TYPES);

/** Os itens por tipo: um acionamento pode ter vários, e a conta é de itens. */
function ItemTypesChart({ query, days }: { query: Query; days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const data = query.data;
  const total = data ? data.itemTypes.reduce((n, t) => n + t.total, 0) : 0;
  return (
    <PanelCard
      className="@container"
      title="Itens por tipo"
      descricao="As peças dos acionamentos do período. Um acionamento pode ter mais de um item."
      query={query}
      empty={
        total === 0
          ? `Nenhum item nos acionamentos abertos nos últimos ${days} dias.`
          : null
      }
      asTable={asTable}
      onToggle={() => setAsTable((v) => !v)}
      skeleton="h-64"
    >
      {data &&
        (asTable ? (
          <Table
            columns={
              [
                {
                  title: 'Tipo',
                  cell: (r) => peripheralItemTypeLabel(r.type),
                  alignment: 'inicio',
                },
                { title: 'Itens', cell: (r) => r.total, alignment: 'fim' },
                {
                  title: '%',
                  cell: (r) => `${share(r.total, total)}%`,
                  alignment: 'fim',
                },
              ] satisfies Column<Data['itemTypes'][number]>[]
            }
            items={data.itemTypes}
            rowKey={(r) => r.type}
            label={`Itens por tipo nos últimos ${data.days} dias`}
            empty="Nenhum item no período."
          />
        ) : (
          <DonutFigure
            label="Itens por tipo"
            noun={['item', 'itens']}
            slices={data.itemTypes.map((t) => ({
              key: t.type,
              label: peripheralItemTypeLabel(t.type),
              total: t.total,
              tone: donutToneAt(ITEM_TYPE_ORDER.indexOf(t.type)),
            }))}
          />
        ))}
    </PanelCard>
  );
}

/* ------------------------------------------------------------------------ */

const PERIPHERAL_NOUN: StateNoun = ['acionamento', 'acionamentos'];

/** Os acionamentos por estado, pela UF do local do serviço. */
function StatesChart({ query, days }: { query: Query; days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const data = query.data;
  const states = data?.states;
  const located = states ? states.states.reduce((n, s) => n + s.total, 0) : 0;
  return (
    <PanelCard
      className="@container"
      title="Acionamentos por estado"
      descricao="Pela UF do local do serviço."
      query={query}
      empty={
        data?.total === 0 ? `Nenhum acionamento aberto nos últimos ${days} dias.` : null
      }
      asTable={asTable}
      onToggle={() => setAsTable((v) => !v)}
      toggleLabel="mapa"
      skeleton="h-80"
      caption={
        data &&
        states && (
          <>
            {count(located, 'acionamento está', 'acionamentos estão')} no mapa, de{' '}
            {data.total} abertos no período.
            {states.withoutLocation > 0 &&
              ` ${count(states.withoutLocation, 'não tem', 'não têm')} local do serviço gravado.`}
            {states.unknownState > 0 &&
              ` ${count(states.unknownState, 'tem', 'têm')} um estado que não é UF.`}
          </>
        )
      }
    >
      {states &&
        (asTable ? (
          <Table
            columns={
              [
                { title: 'Estado', cell: (r) => r.state, alignment: 'inicio' },
                { title: 'Acionamentos', cell: (r) => r.total, alignment: 'fim' },
                {
                  title: '% dos que têm local',
                  cell: (r) => `${share(r.total, located)}%`,
                  alignment: 'fim',
                },
              ] satisfies Column<Data['states']['states'][number]>[]
            }
            items={states.states}
            rowKey={(r) => r.state}
            label={`Acionamentos por estado nos últimos ${days} dias`}
            empty="Nenhum acionamento com local do serviço no período."
          />
        ) : located === 0 ? (
          <p className="py-10 text-center text-sm text-texto-suave">
            Nenhum acionamento do período tem o local do serviço gravado.
          </p>
        ) : (
          <div className="grid items-start gap-5 @lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
            <StatesFigure
              states={states.states}
              located={located}
              noun={PERIPHERAL_NOUN}
            />
            <Ranking states={states.states} located={located} noun={PERIPHERAL_NOUN} />
          </div>
        ))}
    </PanelCard>
  );
}
